import type {
  ListingsAPIResponseFacet,
  ListingsAPIResponseNumericFacetValue,
  ListingsAPIResponseTextFacetValue,
} from '@noths/polaris-client-listings-api';

import type { TextFacetConfig } from 'src/adapters/configToStore/listings-api/listingsControlsConfig.types';
import type { Facet, FacetOption } from 'src/redux/productsQuery/types/Facet';
import { logger } from 'src/utils/serverLogger';
import { getUpdatedOptionsForNumericFacet } from './getUpdatedOptionsForNumericFacet';
import { getUpdatedOptionsForTextFacet } from './getUpdatedOptionsForTextFacet';

const mapNumericOptions = (values: ListingsAPIResponseNumericFacetValue[]): FacetOption[] => {
  return values.map((value) => {
    const { maximum: max = null, minimum: min = null } = value.interval || {};

    return {
      active: false,
      count: parseInt(value.count, 10),
      title: `${min ? min : ''} - ${max ? max : ''}`,
      value: `${min ? min : ''}-${max ? max : ''}`,
    };
  });
};

const mapTextOptions = (
  facetConfig: TextFacetConfig,
  values: ListingsAPIResponseTextFacetValue[],
): FacetOption[] => {
  if (facetConfig.allowedValues) {
    return values
      .map((value) => {
        const textValue = value.value;
        const allowedValue = facetConfig.allowedValues?.find((av) => av.value === textValue);

        return allowedValue
          ? {
              active: false,
              count: parseInt(value.count, 10),
              title: allowedValue.title,
              value: textValue,
            }
          : null;
      })
      .filter(Boolean) as FacetOption[];
  }

  return values.map((value) => ({
    active: false,
    count: parseInt(value.count, 10),
    title: value.value,
    value: value.value,
  }));
};

const zeroOptionCounts = (facet: Facet): Facet => ({
  ...facet,
  options: facet.options.map((option) => ({ ...option, count: 0 })),
});

const orderFacetsByResponseThenStore = (
  facetByKey: Map<string, Facet>,
  listingsFacets: ListingsAPIResponseFacet[],
  storeFacets: Facet[],
): Facet[] => {
  if (listingsFacets.length === 0) {
    return storeFacets
      .map((sf) => facetByKey.get(sf.key))
      .filter((f): f is Facet => f !== undefined);
  }

  const ordered: Facet[] = [];
  const used = new Set<string>();

  for (const listingFacet of listingsFacets) {
    const facet = facetByKey.get(listingFacet.key);
    if (facet) {
      ordered.push(facet);
      used.add(listingFacet.key);
    }
  }

  for (const storeFacet of storeFacets) {
    const facet = facetByKey.get(storeFacet.key);
    if (facet && !used.has(storeFacet.key)) {
      ordered.push(facet);
      used.add(storeFacet.key);
    }
  }

  for (const [, facet] of facetByKey) {
    if (!used.has(facet.key)) {
      ordered.push(facet);
      used.add(facet.key);
    }
  }

  return ordered;
};

interface AdaptResponseFacetsToStoreOptions {
  listingsFacets: ListingsAPIResponseFacet[];
  storeFacets: Facet[];
}

export const adaptResponseFacetsToStore = ({
  listingsFacets,
  storeFacets,
}: AdaptResponseFacetsToStoreOptions): Facet[] => {
  const facetByKey = new Map<string, Facet>();

  for (const storeFacet of storeFacets) {
    const listingsFacet = listingsFacets.find((facet) => facet.key === storeFacet.key);

    if (!listingsFacet) {
      facetByKey.set(storeFacet.key, zeroOptionCounts(storeFacet));
      continue;
    }

    let options: FacetOption[] = [];

    if (storeFacet.valueType === 'numeric') {
      options = getUpdatedOptionsForNumericFacet(
        storeFacet,
        listingsFacet.values as ListingsAPIResponseNumericFacetValue[],
      );
    }

    if (storeFacet.valueType === 'text') {
      options = getUpdatedOptionsForTextFacet(
        storeFacet,
        listingsFacet.values as ListingsAPIResponseTextFacetValue[],
      );
    }

    facetByKey.set(storeFacet.key, {
      ...storeFacet,
      options,
    });
  }

  listingsFacets.forEach((listingsFacet) => {
    if (facetByKey.has(listingsFacet.key)) {
      return;
    }

    const facetConfigEntry = storeFacets.find((facet) => facet.key === listingsFacet.key);

    if (!facetConfigEntry) {
      logger.warn(
        `dynamic facet received for attribute "${listingsFacet.key}" from listings-api but no matching facet config found`,
      );
      return;
    }

    const firstValue = listingsFacet.values?.[0];

    if (!firstValue) {
      return;
    }

    const facetIsNumeric = 'interval' in firstValue;
    let options: FacetOption[] = [];

    if (facetIsNumeric) {
      options = mapNumericOptions(listingsFacet.values as ListingsAPIResponseNumericFacetValue[]);
    } else {
      options = mapTextOptions(
        facetConfigEntry as TextFacetConfig,
        listingsFacet.values as ListingsAPIResponseTextFacetValue[],
      );
    }

    if (options.length) {
      facetByKey.set(listingsFacet.key, {
        alias: facetConfigEntry.alias,
        key: listingsFacet.key,
        options,
        title: facetConfigEntry.title,
        type: facetConfigEntry.type || 'multi',
        valueType: facetIsNumeric ? 'numeric' : 'text',
        ...('allowedValues' in facetConfigEntry ? { disallowNewValues: true } : {}),
        ...(facetConfigEntry.singleOptionOnly === true ? { singleOptionOnly: true } : {}),
      });
    }
  });

  return orderFacetsByResponseThenStore(facetByKey, listingsFacets, storeFacets);
};
